
import React, { useState, useEffect, useRef } from 'react';
import { Chapter, StoryMode, AppStatus, ChapterPlan } from '../src/types';
import { generateSpeech } from '../services/gemini';
import { resolveChapterTitle } from '../services/chapterTitle.service';
import { resolveChapterSummary } from '../src/utils/chapterSummaryResolver';

interface StoryFeedProps {
  chapters: Chapter[];
  currentChapterId: number;
  mode: StoryMode;
  onRewrite: (chapterId: number) => void;
  onLoadChapter: (chapterId: number) => void;
  isRewriting: boolean;
  status?: AppStatus;
  activeProgress?: { chapterNum: number; agentName: string; elapsedSecs: number; reassuranceMsg: string; requestId?: string } | null;
  chapterPlans?: ChapterPlan[];
  isBookComplete?: boolean;
  showAgentDebug?: boolean;
  chapterLoadErrors?: Record<number, string>;
}

const StoryFeed: React.FC<StoryFeedProps> = ({ chapters, currentChapterId, mode, onRewrite, isRewriting, onLoadChapter, status, activeProgress, chapterPlans, isBookComplete, showAgentDebug, chapterLoadErrors = {} }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const activeChapterNumber = activeProgress?.chapterNum ?? currentChapterId ?? null;
  
  const [audioState, setAudioState] = useState<{
    chapterId: number | null;
    status: 'idle' | 'loading' | 'playing' | 'paused';
  }>({ chapterId: null, status: 'idle' });

  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const queueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const isFetchingRef = useRef(false);

  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chapters]);

  const stopReading = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.onended = null;
      try { sourceNodeRef.current.stop(); } catch (e) {}
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
    }
    queueRef.current = [];
    isPlayingRef.current = false;
    isFetchingRef.current = false;
    setAudioState({ chapterId: null, status: 'idle' });
  };

  useEffect(() => {
    return () => stopReading();
  }, []);

  const playNextChunk = () => {
    if (queueRef.current.length === 0) {
      isPlayingRef.current = false;
      if (!isFetchingRef.current) {
        setAudioState({ chapterId: null, status: 'idle' });
      }
      return;
    }

    isPlayingRef.current = true;
    const base64 = queueRef.current.shift()!;
    
    try {
      const binaryString = atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const int16Array = new Int16Array(bytes.buffer);
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768.0;
      }

      if (!audioCtxRef.current) return;

      const audioBuffer = audioCtxRef.current.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      const source = audioCtxRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtxRef.current.destination);
      sourceNodeRef.current = source;

      source.onended = () => {
        playNextChunk();
      };

      source.start();
    } catch (err) {
      console.error("Audio playback error", err);
      playNextChunk();
    }
  };

  const handlePlayPause = async (chapter: Chapter) => {
    if (audioState.chapterId === chapter.id) {
      if (audioState.status === 'playing') {
        audioCtxRef.current?.suspend();
        setAudioState({ ...audioState, status: 'paused' });
      } else if (audioState.status === 'paused') {
        audioCtxRef.current?.resume();
        setAudioState({ ...audioState, status: 'playing' });
      }
      return;
    }

    // Start new reading
    stopReading();
    setAudioState({ chapterId: chapter.id, status: 'loading' });
    
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    audioCtxRef.current = new AudioContextClass({ sampleRate: 24000 });
    
    if (!chapter.content) return;
    const paragraphs = chapter.content.split('\n').filter((p: string) => p.trim().length > 0);
    isFetchingRef.current = true;
    
    const fetchChunks = async () => {
      for (const p of paragraphs) {
        if (signal.aborted) break;
        try {
          const base64 = await generateSpeech(p);
          if (base64 && !signal.aborted) {
            queueRef.current.push(base64);
            if (!isPlayingRef.current) {
              setAudioState(prev => prev.chapterId === chapter.id ? { chapterId: chapter.id, status: 'playing' } : prev);
              playNextChunk();
            }
          }
        } catch (err) {
          console.error("Failed to fetch TTS chunk", err);
        }
      }
      isFetchingRef.current = false;
      if (!isPlayingRef.current && !signal.aborted) {
        setAudioState({ chapterId: null, status: 'idle' });
      }
    };

    fetchChunks();
  };

  const handleStop = () => {
    stopReading();
  };

  const renderContent = (chapter: Chapter) => {
    const chapterError = chapterLoadErrors[chapter.id];
    if (!chapter.content) {
      return (
        <div className="flex flex-col items-center my-8 gap-3">
          {chapterError && (
            <div className="w-full max-w-xl p-3 rounded border border-cv-warning/40 bg-cv-warning/10 text-cv-warning">
              <div className="text-xs font-bold">{chapterError}</div>
              {showAgentDebug && (
                <div className="text-[10px] mt-1 text-cv-warning/80">Storage read blocked; backend fallback failed.</div>
              )}
            </div>
          )}
          <button
            onClick={() => onLoadChapter(chapter.id)}
            className="px-6 py-2 bg-cv-elevated hover:bg-cv-elevated border border-cv-border-strong text-cv-gold text-xs font-bold uppercase tracking-widest rounded transition-colors"
          >
            {chapterError ? 'Try Again' : 'Read Full Chapter'}
          </button>
        </div>
      );
    }
    const content = chapter.content;
    if (mode === StoryMode.COMIC_SCRIPT) {
      return content.split('\n').map((line, i) => {
        const isPage = line.trim().toUpperCase().startsWith('PAGE');
        const isPanel = line.trim().toUpperCase().startsWith('PANEL');
        const isDialogue = line.includes(':');

        return (
          <div key={i} className={`mb-1 ${isPage ? 'text-cv-gold font-black mt-6 border-b border-cv-border pb-2' : ''} ${isPanel ? 'text-cv-interactive font-bold mt-3 underline' : ''} ${isDialogue ? 'pl-8' : ''}`}>
             <span className={isDialogue ? 'text-cv-muted italic' : 'text-cv-secondary'}>{line}</span>
          </div>
        );
      });
    }
    return <div className="text-cv-secondary whitespace-pre-line leading-loose md:leading-relaxed font-serif text-base md:text-sm break-words">{content}</div>;
  };

  return (
    <div className="bg-cv-panel border border-cv-border-strong rounded-lg p-6 h-full flex flex-col relative overflow-hidden">
       <div className="absolute top-0 right-0 p-2">
         <span className="text-[10px] font-mono font-black text-cv-disabled uppercase border border-cv-border px-2 py-0.5 rounded bg-cv-page">{mode} ENGINE ACTIVE</span>
       </div>
       {isBookComplete && (
         <div className="mb-4 p-3 bg-cv-success/10 border border-cv-success/30 rounded-lg text-xs font-bold text-cv-success flex items-center gap-2">
           <span>🎉</span>
           <span>Volume complete — review your chapters or export the manuscript.</span>
         </div>
       )}
       <div className="flex items-center justify-between mb-4">
         <h3 className="text-sm uppercase tracking-wider text-cv-muted font-bold flex items-center">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          Manuscript Chapters
        </h3>
       </div>
      <div ref={containerRef} className="flex-1 overflow-y-auto space-y-8 pr-2 custom-scrollbar">
        {chapters.length === 0 ? (
          <div className="h-full flex items-center justify-center text-cv-disabled italic font-mono text-xs uppercase tracking-widest animate-pulse">
            Establishing Link with Chronovela...
          </div>
        ) : (
          chapters.map((chapter, index) => {
            const chapterId = chapter.id ?? chapter.chapterNumber ?? (index + 1);
            const isActive = chapterId === activeChapterNumber && status !== AppStatus.IDLE;
            const roadmapPlan = chapterPlans?.find(p => p.chapterNumber === chapterId || p.chapterNumber === chapter.chapterNumber);
            const resolvedTitle = resolveChapterTitle(chapter, roadmapPlan, chapterId);
            const resolvedSummary = resolveChapterSummary({
              storedSummary: chapter.summary,
              roadmapSummary: roadmapPlan?.summary,
              recoverySummary: (chapter as any).recoverySummary,
              existingSummary: chapter.summary,
            });
            return (
              <div key={chapterId} className="max-w-none border-b border-cv-border pb-10 last:border-0 relative group">
                <div className="flex items-center justify-between mb-6">
                   <div className="flex items-center gap-4">
                       <div className="w-10 h-10 bg-cv-success/20 text-cv-gold rounded-lg flex items-center justify-center font-black text-xl border border-cv-interactive/30">
                        {chapterId}
                       </div>
                      <div>
                        <h2 className="text-2xl font-black text-cv-primary uppercase tracking-tighter">{resolvedTitle}</h2>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${
                            isActive
                              ? 'bg-cv-success/20 border-cv-interactive text-cv-gold animate-pulse'
                              : 'bg-cv-success/20 border-cv-success text-cv-success'
                          }`}>
                            {isActive ? 'In Progress' : 'Complete'}
                          </span>
                          {isActive && activeProgress && (
                            <span className="text-[10px] text-cv-interactive font-mono animate-pulse">
                              • {showAgentDebug ? `${activeProgress.agentName} (${activeProgress.elapsedSecs}s) • ` : ''}{activeProgress.reassuranceMsg}
                            </span>
                          )}
                        </div>
                      </div>
                   </div>
                   <div className="flex items-center gap-2">
                     <button 
                      onClick={() => onRewrite(chapterId)} 
                      disabled={isRewriting}
                      className={`transition-all px-3 py-1.5 rounded text-xs font-black uppercase flex items-center gap-2 ${
                        isRewriting 
                          ? 'bg-cv-elevated text-cv-disabled cursor-not-allowed border border-cv-border-strong'
                          : 'bg-cv-gold/10 text-cv-interactive border border-cv-gold/30 hover:bg-cv-gold/15'
                      }`}
                     >
                       <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                       </svg>
                       Rewrite
                     </button>
                   </div>
                </div>
                <div className="font-mono text-base md:text-sm break-words">
                  {renderContent(chapter)}
                </div>
                <div className="mt-8 p-4 bg-cv-page rounded-xl border border-cv-border/50 text-xs text-cv-muted font-mono relative">
                  <div className="absolute -top-2 left-4 px-2 bg-cv-page text-[10px] font-black uppercase text-cv-disabled">Chapter Summary</div>
                  {resolvedSummary}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StoryFeed;
